/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'

import { DeviceListOptions, useDevice, useReader } from 'components/reader'
import { DeviceConnectError } from 'components/dme/errors/DeviceConnectError'
import config from 'config/app'
import versionConfig from 'config/version.json'
import { useAppSelector } from 'hooks/redux'
import { Order } from 'store/types'

const host = config?.newApi
// Lab-side upload endpoint (mirrors the DME reader upload, but authorised for lab staff).
const getUploadEndpoint = (orderId: string | number) => `${host}/lab/orders/${orderId}/device-data`

enum Step {
  DETECT = 0,
  DEVICE_LIST = 1,
  UPLOADING = 2,
  DONE = 3,
}

interface IProps {
  isOpen: boolean
  onClose: () => void
  order: Order
  refetchOrder?: () => void
}

export const CheckMeO2PpgUploadModal = ({ isOpen, onClose, order, refetchOrder }: IProps) => {
  const token = localStorage.getItem('access_token')
  const init = useAppSelector((state) => state.init)
  const orderId = order?.id

  const [step, setStep] = useState<Step>(Step.DETECT)
  const [devices, setDevices] = useState<DeviceListOptions[] | null>(null)
  const [selectedDevice, setSelectedDevice] = useState<DeviceListOptions>()
  const [pendingUpload, setPendingUpload] = useState(false)
  // `type` distinguishes the reader-not-running case ('ServiceError') from ordinary upload errors.
  const [error, setError] = useState<{ type?: string; message: string } | null>(null)

  const { reader } = useReader()
  const device = useDevice({
    selectedDevice,
    deviceType: order?.device?.model,
    orderId: String(orderId),
    token,
  })
  const deviceRef = useRef(device)
  deviceRef.current = device

  useEffect(() => {
    if (pendingUpload && device?.device) {
      setPendingUpload(false)
      deviceRef.current.read()
    }
  }, [pendingUpload, device?.device])

  const connect = () => {
    setError(null)
    setDevices(null)
    setStep(Step.DETECT)
    reader.disconnect()
    reader.connect()

    reader.on('connect', () => {
      reader.send()
      reader.sendClientInfo({
        clientVersion: versionConfig.version,
        userId: init?.id,
        username: init?.profile?.email,
      })
      reader.getConnectionList()
    })

    reader.on('deviceList', (deviceList: DeviceListOptions[]) => {
      setDevices(deviceList)
      setStep(Step.DEVICE_LIST)
      reader.setUploadEndpoint(getUploadEndpoint(orderId))
    })

    reader.on('uploadCompleted', () => {
      reader.disconnect()
      setStep(Step.DONE)
      refetchOrder?.()
    })

    reader.on('socketError', (e: string) => setError({ type: 'SocketError', message: e }))
    reader.on('uploadError', (e: string) => setError({ type: 'UploadError', message: `Device upload failed. ${e}` }))
    reader.on('uploadRejected', (e: string) => setError({ type: 'UploadRejected', message: e }))
    // The reader emits 'error' with type 'ServiceError' when the TestSmarter Reader app isn't running.
    reader.on('error', (e: { type?: string; message?: string } | string) => {
      if (typeof e === 'string') setError({ message: e })
      else setError({ type: e?.type, message: e?.message ?? 'Reader error' })
    })
  }

  const handleClose = () => {
    reader.disconnect()
    setStep(Step.DETECT)
    setDevices(null)
    setSelectedDevice(undefined)
    setError(null)
    onClose()
  }

  const startUpload = (d: DeviceListOptions) => {
    setError(null)
    setSelectedDevice(d)
    setStep(Step.UPLOADING)
    setPendingUpload(true)
  }

  return (
    <Modal show={isOpen} onHide={handleClose} centered>
      <Modal.Header>
        <Modal.Title>Upload CheckMe O2 (PPG) data</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error?.type === 'ServiceError' ? (
          <DeviceConnectError onReturn={connect} hideSupportContact />
        ) : error ? (
          <div>
            <div className="alert alert-danger" role="alert">
              {error.message}
            </div>
            <Button variant="outline-primary" onClick={connect}>
              <i className="fas fa-undo mr-2" /> Try again
            </Button>
          </div>
        ) : step === Step.DETECT ? (
          <div>
            <p>
              Plug the returned device into this computer, then detect it with the TestSmarter
              Reader.
            </p>
            <Button onClick={connect}>Detect Device(s)</Button>
          </div>
        ) : step === Step.DEVICE_LIST ? (
          <div className="d-flex flex-column align-items-start">
            {devices?.length ? (
              <>
                <p>Select the connected device to upload its data:</p>
                {devices.map((d) => (
                  <Button
                    key={d.connectionId + d.connectionPath}
                    className="mb-2"
                    css={{ width: '100%' }}
                    onClick={() => startUpload(d)}>
                    Upload - {d.optionLabel}
                  </Button>
                ))}
              </>
            ) : (
              <div>
                <h5>No devices detected.</h5>
                <Button onClick={connect}>
                  <i className="fas fa-undo mr-2" /> Retry Detecting Devices
                </Button>
              </div>
            )}
          </div>
        ) : step === Step.UPLOADING ? (
          <div>
            <p className="d-flex align-items-center">
              <Spinner as="span" animation="border" size="sm" role="status" className="mr-2" />
              Status: <strong className="ml-1">{device?.status}</strong>
            </p>
            <p>
              Device read data: <strong>{device?.progress ?? 0} bytes</strong>
            </p>
          </div>
        ) : (
          <div>
            <div className="alert alert-success" role="alert">
              Data uploaded for order <strong>{orderId}</strong> and sent to EnsoData for scoring.
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {step === Step.DONE ? 'Close' : 'Cancel'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default CheckMeO2PpgUploadModal
